import React, { FC, useState } from 'react';
import { useTranslation } from '@wix/yoshi-flow-editor';
import { Dropdown, type DropdownOptionProps } from 'wix-ui-tpa/cssVars';
import { classes, st } from './LocationDropdown.st.css';
import { Confirm, Location } from '@wix/wix-ui-icons-common';
import { ChevronDown } from '@wix/wix-ui-icons-common/on-stage';
import { RentalsListDataHooks } from '../dataHooks';
import type { LocationOption } from '../../../../types/types';

export type LocationDropdownProps = {
  /** Selected location id, or `''` for "all locations". */
  value: string;
  onChange: (locationId: string) => void;
  locations: LocationOption[];
  disabled?: boolean;
};

const ALL_LOCATIONS = 'all-locations';

/**
 * Location picker.
 *
 * Uses the library `Dropdown` for the menu itself — it brings keyboard
 * navigation, focus management and the mobile native-select fallback. The
 * selected row's checkmark rides on each option's `suffix`. The pin and
 * chevron both sit outside as overlays: the trigger has no icon slot, and
 * the library's own chevron has no independent color token, so it's hidden
 * (see LocationDropdown.st.css) in favor of this one.
 */
export const LocationDropdown: FC<LocationDropdownProps> = ({
  value,
  onChange,
  locations,
  disabled,
}) => {
  const { t } = useTranslation();
  const allLabel = t('widget.servicesList.searchBar.locationPlaceholder');
  const selectedId = value || ALL_LOCATIONS;
  const [isOpen, setIsOpen] = useState(false);

  const options: DropdownOptionProps[] = [
    { id: ALL_LOCATIONS, value: allLabel, isSelectable: true },
    ...locations.map((location) => ({
      id: location.id,
      value: location.name,
      isSelectable: true,
    })),
  ].map((option) => ({
    ...option,
    suffix:
      option.id === selectedId ? (
        <Confirm className={classes.check} />
      ) : undefined,
  }));

  return (
    <div className={classes.root}>
      <Location className={classes.icon} />
      <Dropdown
        className={st(classes.dropdown, {
          isPlaceholder: selectedId === ALL_LOCATIONS,
        })}
        options={options}
        selectedId={selectedId}
        placeholder={allLabel}
        aria-label={allLabel}
        disabled={disabled}
        data-hook={RentalsListDataHooks.searchLocation}
        onChange={(option) =>
          onChange(option.id === ALL_LOCATIONS ? '' : option.id ?? '')
        }
        onExpandedChange={setIsOpen}
      />
      <ChevronDown className={st(classes.chevron, { isOpen })} />
    </div>
  );
};
